import React, { useCallback, useEffect, useState } from 'react';
import { type LayoutChangeEvent, StyleSheet, View } from 'react-native';

import { BagFeaturedUpsellConfettiPiece } from '../BagFeaturedUpsellConfettiPiece';

// ─────────────────────────────────────────────────────────────────────────────

export const BagFeaturedUpsellConfetti = (props: Props) => {
  const { imageUrl, onFinish } = props;

  // ─── State ─────────────────────────────────────────────────────────────────

  const [items] = useState(createItems);
  const [containerSize, setContainerSize] = useState<ContainerSize | undefined>(undefined);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const measureContainer = useCallback((event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;

    setContainerSize({ width, height });
  }, []);

  const scheduleFinish = useCallback(() => {
    const timeout = setTimeout(onFinish, ANIMATION_DURATION);

    return () => {
      clearTimeout(timeout);
    };
  }, [onFinish]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(scheduleFinish, [scheduleFinish]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View aria-hidden pointerEvents="none" style={styles.container} onLayout={measureContainer}>
      {containerSize
        ? items.map((item) => (
            <BagFeaturedUpsellConfettiPiece
              key={item.id}
              imageUrl={imageUrl}
              containerWidth={containerSize.width}
              containerHeight={containerSize.height}
              origin={item.origin}
              drift={item.drift}
              peak={item.peak}
              size={item.size}
              delay={item.delay}
              riseDuration={item.riseDuration}
              fallDuration={item.fallDuration}
              rotation={item.rotation}
            />
          ))
        : null}
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function createItems() {
  return Array.from({ length: ITEMS_COUNT }, createItem);
}

function createItem(_: unknown, index: number): Item {
  return {
    id: index,
    origin: getRandomNumber(MIN_ORIGIN_PERCENT, MAX_ORIGIN_PERCENT),
    drift: getRandomNumber(-MAX_DRIFT, MAX_DRIFT),
    peak: getRandomNumber(MIN_PEAK, MAX_PEAK),
    size: getRandomNumber(MIN_SIZE, MAX_SIZE),
    delay: getRandomNumber(0, MAX_DELAY),
    riseDuration: getRandomNumber(MIN_RISE_DURATION, MAX_RISE_DURATION),
    fallDuration: getRandomNumber(MIN_FALL_DURATION, MAX_FALL_DURATION),
    rotation: getRandomNumber(-MAX_ROTATION, MAX_ROTATION),
  };
}

function getRandomNumber(min: number, max: number) {
  return min + Math.random() * (max - min);
}

// ─── Constants ───────────────────────────────────────────────────────────────

const ITEMS_COUNT = 40;
const MIN_ORIGIN_PERCENT = 44;
const MAX_ORIGIN_PERCENT = 52;
const MAX_DRIFT = 0.45;
const MIN_PEAK = 0.45;
const MAX_PEAK = 0.9;
const MIN_SIZE = 20;
const MAX_SIZE = 32;
const MAX_DELAY = 150;
const MIN_RISE_DURATION = 550;
const MAX_RISE_DURATION = 800;
const MIN_FALL_DURATION = 1400;
const MAX_FALL_DURATION = 2000;
const MAX_ROTATION = 540;
const ANIMATION_DURATION = MAX_DELAY + MAX_RISE_DURATION + MAX_FALL_DURATION;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFill,
    zIndex: 1,
    overflow: 'hidden',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  imageUrl: string;
  onFinish: () => void;
};

type Item = {
  id: number;
  origin: number;
  drift: number;
  peak: number;
  size: number;
  delay: number;
  riseDuration: number;
  fallDuration: number;
  rotation: number;
};

type ContainerSize = {
  width: number;
  height: number;
};
